import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';

const BehaviorSection = ({ tiktokId }) => {
  const { t } = useTranslation();
  const DAYS = [
    t('admin.creators.dayAbbr.sun', 'Dim'),
    t('admin.creators.dayAbbr.mon', 'Lun'),
    t('admin.creators.dayAbbr.tue', 'Mar'),
    t('admin.creators.dayAbbr.wed', 'Mer'),
    t('admin.creators.dayAbbr.thu', 'Jeu'),
    t('admin.creators.dayAbbr.fri', 'Ven'),
    t('admin.creators.dayAbbr.sat', 'Sam'),
  ];
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tiktokId) return;
    const fetch = async () => {
      setLoading(true);
      try {
        // Fetch last 90 days of sessions
        const since = new Date(Date.now() - 90 * 24 * 3600 * 1000).toISOString();
        const { data } = await supabase
          .from('creator_live_sessions')
          .select('start_time, duration_seconds, diamonds, viewers, pcu, acu, gifters, is_live_match, is_multi_guest, avg_watch_duration_seconds')
          .eq('tiktok_id', tiktokId)
          .gte('start_time', since)
          .order('start_time', { ascending: false });
        if (data) setSessions(data);
      } catch (err) {
        console.error('Behavior fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [tiktokId]);

  const behavior = useMemo(() => {
    if (!sessions.length) return null;

    // --- Horaire habituel ---
    const hourCounts = Array(24).fill(0);
    const dayCounts = Array(7).fill(0);
    sessions.forEach(s => {
      if (!s.start_time) return;
      const d = new Date(s.start_time);
      hourCounts[d.getUTCHours()]++;
      dayCounts[d.getUTCDay()]++;
    });
    // Peak hour range (top 4 consecutive hours)
    let bestStart = 0, bestSum = 0;
    for (let i = 0; i < 24; i++) {
      const sum = hourCounts[i] + hourCounts[(i + 1) % 24] + hourCounts[(i + 2) % 24] + hourCounts[(i + 3) % 24];
      if (sum > bestSum) { bestSum = sum; bestStart = i; }
    }
    const peakHourRange = `${bestStart}h - ${(bestStart + 4) % 24}h`;

    // Active days (days with > 15% of sessions)
    const threshold = sessions.length * 0.12;
    const activeDays = DAYS.filter((_, i) => dayCounts[i] > threshold);

    // --- Durée typique ---
    const durations = sessions.map(s => s.duration_seconds || 0).filter(d => d > 0).sort((a, b) => a - b);
    const medianDuration = durations.length > 0 ? durations[Math.floor(durations.length / 2)] : 0;

    // --- Fréquence ---
    const firstSession = new Date(sessions[sessions.length - 1].start_time);
    const lastSession = new Date(sessions[0].start_time);
    const weeks = Math.max(1, (lastSession - firstSession) / (7 * 24 * 3600 * 1000));
    const sessionsPerWeek = sessions.length / weeks;

    // --- Régularité (coefficient de variation des intervalles) ---
    const intervals = [];
    for (let i = 0; i < sessions.length - 1; i++) {
      const diff = new Date(sessions[i].start_time) - new Date(sessions[i + 1].start_time);
      intervals.push(diff / 3600000); // in hours
    }
    let regularity = 'N/A';
    if (intervals.length >= 3) {
      const mean = intervals.reduce((s, v) => s + v, 0) / intervals.length;
      const std = Math.sqrt(intervals.reduce((s, v) => s + (v - mean) ** 2, 0) / intervals.length);
      const cv = mean > 0 ? std / mean : 0;
      if (cv < 0.4) regularity = t('admin.creators.regularityValues.veryRegular', 'Très régulier');
      else if (cv < 0.7) regularity = t('admin.creators.regularityValues.regular', 'Régulier');
      else if (cv < 1.2) regularity = t('admin.creators.regularityValues.variable', 'Variable');
      else regularity = t('admin.creators.regularityValues.irregular', 'Irrégulier');
    }

    // --- Format ---
    const pkCount = sessions.filter(s => s.is_live_match).length;
    const multiCount = sessions.filter(s => s.is_multi_guest).length;
    const soloCount = sessions.length - pkCount - multiCount;
    const pkPct = Math.round((pkCount / sessions.length) * 100);
    const multiPct = Math.round((multiCount / sessions.length) * 100);
    const soloPct = 100 - pkPct - multiPct;

    // --- Engagement moyen ---
    const withGifters = sessions.filter(s => s.viewers > 0);
    const avgGifterRate = withGifters.length > 0
      ? withGifters.reduce((s, r) => s + (r.gifters || 0) / r.viewers, 0) / withGifters.length * 100
      : 0;

    // --- Watch duration moyen ---
    const withWatch = sessions.filter(s => s.avg_watch_duration_seconds > 0);
    const avgWatch = withWatch.length > 0
      ? withWatch.reduce((s, r) => s + r.avg_watch_duration_seconds, 0) / withWatch.length
      : 0;

    // --- Changements de comportement (semaine en cours vs 4 dernières semaines) ---
    const now = Date.now();
    const oneWeek = 7 * 24 * 3600 * 1000;
    const thisWeek = sessions.filter(s => (now - new Date(s.start_time).getTime()) < oneWeek);
    const prev4Weeks = sessions.filter(s => {
      const age = now - new Date(s.start_time).getTime();
      return age >= oneWeek && age < 5 * oneWeek;
    });
    const prev4WeeksPerWeek = prev4Weeks.length / 4;

    const changes = [];

    // Frequency change
    if (prev4WeeksPerWeek >= 2) {
      const freqRatio = thisWeek.length / prev4WeeksPerWeek;
      if (freqRatio < 0.3) changes.push({ type: 'critical', text: t('admin.creators.changeFreqDrop', 'Fréquence en chute : {{thisWeek}} session(s) cette semaine vs {{avg}}/sem en moyenne', { thisWeek: thisWeek.length, avg: prev4WeeksPerWeek.toFixed(1) }) });
      else if (freqRatio < 0.6) changes.push({ type: 'warning', text: t('admin.creators.changeFreqDown', 'Fréquence en baisse : {{thisWeek}} session(s) cette semaine vs {{avg}}/sem', { thisWeek: thisWeek.length, avg: prev4WeeksPerWeek.toFixed(1) }) });
      else if (freqRatio > 1.5) changes.push({ type: 'positive', text: t('admin.creators.changeFreqUp', 'Fréquence en hausse : {{thisWeek}} session(s) cette semaine vs {{avg}}/sem', { thisWeek: thisWeek.length, avg: prev4WeeksPerWeek.toFixed(1) }) });
    }

    // Duration change
    if (thisWeek.length >= 2 && prev4Weeks.length >= 4) {
      const avgDurThis = thisWeek.reduce((s, r) => s + (r.duration_seconds || 0), 0) / thisWeek.length;
      const avgDurPrev = prev4Weeks.reduce((s, r) => s + (r.duration_seconds || 0), 0) / prev4Weeks.length;
      if (avgDurPrev > 0) {
        const durRatio = avgDurThis / avgDurPrev;
        if (durRatio < 0.6) changes.push({ type: 'warning', text: t('admin.creators.changeDurDown', 'Durée en baisse : {{current}} moy. vs {{usual}} habituellement', { current: fmtDur(avgDurThis), usual: fmtDur(avgDurPrev) }) });
        else if (durRatio > 1.4) changes.push({ type: 'positive', text: t('admin.creators.changeDurUp', 'Durée en hausse : {{current}} moy. vs {{usual}} habituellement', { current: fmtDur(avgDurThis), usual: fmtDur(avgDurPrev) }) });
      }
    }

    // Diamonds/h change
    if (thisWeek.length >= 2 && prev4Weeks.length >= 4) {
      const dphThis = thisWeek.reduce((s, r) => s + (r.diamonds || 0), 0) / Math.max(1, thisWeek.reduce((s, r) => s + (r.duration_seconds || 0), 0) / 3600);
      const dphPrev = prev4Weeks.reduce((s, r) => s + (r.diamonds || 0), 0) / Math.max(1, prev4Weeks.reduce((s, r) => s + (r.duration_seconds || 0), 0) / 3600);
      if (dphPrev > 0) {
        const dphRatio = dphThis / dphPrev;
        if (dphRatio < 0.6) changes.push({ type: 'warning', text: t('admin.creators.changeRevDown', 'Revenus en baisse : {{current}} diamonds/h vs {{usual}}', { current: Math.round(dphThis).toLocaleString('fr-FR'), usual: Math.round(dphPrev).toLocaleString('fr-FR') }) });
        else if (dphRatio > 1.5) changes.push({ type: 'positive', text: t('admin.creators.changeRevUp', 'Revenus en hausse : {{current}} diamonds/h vs {{usual}}', { current: Math.round(dphThis).toLocaleString('fr-FR'), usual: Math.round(dphPrev).toLocaleString('fr-FR') }) });
      }
    }

    // No session this week but was active
    if (thisWeek.length === 0 && prev4WeeksPerWeek >= 1) {
      changes.push({ type: 'critical', text: t('admin.creators.changeNoSession', 'Aucune session cette semaine (habituellement {{avg}}/sem)', { avg: prev4WeeksPerWeek.toFixed(1) }) });
    }

    return {
      peakHourRange, activeDays, medianDuration, sessionsPerWeek, regularity,
      soloPct, pkPct, multiPct,
      avgGifterRate, avgWatch,
      changes,
      totalSessions: sessions.length,
    };
  }, [sessions, t]);

  const card = (children) => (
    <div style={{ background: '#111111', border: '1px solid #1f1f1f', borderRadius: '12px', padding: '14px' }}>
      {children}
    </div>
  );

  const label = (text) => (
    <p style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'rgba(255,255,255,0.35)', fontFamily: 'Sora, sans-serif', margin: '0 0 10px 0' }}>
      {text}
    </p>
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {[1, 2].map(i => (
          <div key={i} style={{ height: '80px', background: '#111', border: '1px solid #1f1f1f', borderRadius: '12px', animation: 'pulse 2s infinite' }} />
        ))}
      </div>
    );
  }

  if (!behavior) {
    return card(
      <>
        {label(t('admin.creators.behaviorLiveLabel', 'Comportement LIVE'))}
        <p style={{ textAlign: 'center', fontSize: '12px', color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif', padding: '12px 0' }}>
          {t('admin.creators.notEnoughData', 'Pas assez de données (90 derniers jours)')}
        </p>
      </>
    );
  }

  const statBox = (title, value, color = 'white') => (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px' }}>{title}</div>
      <div style={{ fontSize: '14px', fontWeight: '500', color, fontFamily: 'Sora, sans-serif' }}>{value}</div>
    </div>
  );

  const changeColors = { critical: '#ef4444', warning: '#f97316', positive: '#22c55e' };
  const changeIcons = { critical: '🔴', warning: '⚠️', positive: '📈' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>

      {/* Habitudes */}
      {card(
        <>
          {label(t('admin.creators.streamingHabits', 'Habitudes de streaming — 90 derniers jours'))}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '12px' }}>
            {statBox(t('admin.creators.usualSchedule', 'Horaire habituel'), behavior.peakHourRange, '#3b82f6')}
            {statBox(t('admin.creators.activeDays', 'Jours actifs'), behavior.activeDays.length > 0 ? behavior.activeDays.join(', ') : t('admin.creators.regularityValues.variable', 'Variable'), '#22c55e')}
            {statBox(t('admin.creators.regularity', 'Régularité'), behavior.regularity, behavior.regularity === t('admin.creators.regularityValues.veryRegular', 'Très régulier') || behavior.regularity === t('admin.creators.regularityValues.regular', 'Régulier') ? '#22c55e' : behavior.regularity === t('admin.creators.regularityValues.variable', 'Variable') ? '#f97316' : '#ef4444')}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
            {statBox(t('admin.creators.typicalDuration', 'Durée typique'), fmtDur(behavior.medianDuration))}
            {statBox(t('admin.creators.frequency', 'Fréquence'), `${behavior.sessionsPerWeek.toFixed(1)}${t('admin.creators.perWeek', '/sem')}`)}
            {statBox(t('admin.creators.watchAvg', 'Watch moy.'), fmtWatch(behavior.avgWatch), '#eab308')}
          </div>
        </>
      )}

      {/* Format & engagement */}
      {card(
        <>
          {label(t('admin.creators.formatEngagement', 'Format & engagement'))}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            {behavior.soloPct > 0 && (
              <div style={{ flex: behavior.soloPct, height: '6px', borderRadius: '3px', background: '#3b82f6' }} title={`Solo ${behavior.soloPct}%`} />
            )}
            {behavior.pkPct > 0 && (
              <div style={{ flex: behavior.pkPct, height: '6px', borderRadius: '3px', background: '#f97316' }} title={`PK ${behavior.pkPct}%`} />
            )}
            {behavior.multiPct > 0 && (
              <div style={{ flex: behavior.multiPct, height: '6px', borderRadius: '3px', background: '#a855f7' }} title={`Multi ${behavior.multiPct}%`} />
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', fontSize: '11px', fontFamily: 'Inter, sans-serif', color: 'rgba(255,255,255,0.5)', marginBottom: '12px' }}>
            <span><span style={{ color: '#3b82f6' }}>●</span> Solo {behavior.soloPct}%</span>
            <span><span style={{ color: '#f97316' }}>●</span> PK {behavior.pkPct}%</span>
            <span><span style={{ color: '#a855f7' }}>●</span> Multi-guest {behavior.multiPct}%</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
            {statBox(t('admin.creators.avgGifterRate', 'Taux gifter moy.'), `${behavior.avgGifterRate.toFixed(1)}%`, '#22c55e')}
            {statBox(t('admin.creators.analyzedSessions', 'Sessions analysées'), behavior.totalSessions.toString(), 'rgba(255,255,255,0.6)')}
          </div>
        </>
      )}

      {/* Changements détectés */}
      {behavior.changes.length > 0 && card(
        <>
          {label(t('admin.creators.changesDetected', 'Changements détectés'))}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {behavior.changes.map((c, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '8px 10px', borderRadius: '8px',
                background: changeColors[c.type] + '10',
                border: `1px solid ${changeColors[c.type]}30`,
              }}>
                <span style={{ fontSize: '14px' }}>{changeIcons[c.type]}</span>
                <span style={{ fontSize: '12px', color: changeColors[c.type], fontFamily: 'Inter, sans-serif' }}>
                  {c.text}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

function fmtDur(seconds) {
  if (!seconds) return '0h00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${String(m).padStart(2, '0')}min`;
}

function fmtWatch(seconds) {
  if (!seconds) return '—';
  const s = Math.round(seconds);
  if (s >= 60) {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}m${String(sec).padStart(2, '0')}s`;
  }
  return `${s}s`;
}

export default BehaviorSection;
